import { invoke } from '@tauri-apps/api/core'
import { listen, type UnlistenFn } from '@tauri-apps/api/event'

import {
  clearWatch,
  ensureGeolocationPermission,
  watchPosition,
  type GeoError,
  type GeoPosition,
  type GeoWatchHandle,
} from '@/services/geolocationService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { isTauriAndroidMobile, isTauriAppleMobile, isTauriMobile } from '@/services/platformService'
import type { RecordingDraft, RecordingPointInput, RecordingState } from '@/types/recording'

const RECORDING_GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 15000,
}

const DEFAULT_STATE: RecordingState = {
  status: 'idle',
  sessionId: null,
  startedAt: null,
  endedAt: null,
  pointCount: 0,
  segmentCount: 0,
  currentSegmentIndex: 0,
  lastPoint: null,
  autoPauseEnabled: false,
  autoPaused: false,
}

const normalizeState = (state: Partial<RecordingState> | null | undefined): RecordingState => ({
  ...DEFAULT_STATE,
  ...state,
  sessionId: state?.sessionId ?? null,
  startedAt: state?.startedAt ?? null,
  endedAt: state?.endedAt ?? null,
  lastPoint: state?.lastPoint ?? null,
})

const positionToPointInput = (position: GeoPosition): RecordingPointInput => ({
  lat: position.coords.latitude,
  lon: position.coords.longitude,
  ele: position.coords.altitude ?? null,
  time: position.timestamp,
  accuracy: position.coords.accuracy,
  speed: position.coords.speed ?? null,
  heading: position.coords.heading ?? null,
})

class RecordingNativeService {
  private foregroundWatch: GeoWatchHandle | null = null
  private stateUnlisten: UnlistenFn | null = null
  private foregroundWatchStartPromise: Promise<void> | null = null
  private nativePollTimer: ReturnType<typeof setInterval> | null = null
  private nativePollHandler: ((state: RecordingState) => void) | null = null

  isSupported(): boolean {
    return isTauriMobile()
  }

  usesNativePointWriter(): boolean {
    return isTauriAppleMobile() || isTauriAndroidMobile()
  }

  usesForegroundWebWriter(): boolean {
    return false
  }

  async listenForStateChanges(handler: (state: RecordingState) => void): Promise<void> {
    if (this.stateUnlisten) return

    this.stateUnlisten = await listen<RecordingState>('recording-state-changed', (event) => {
      handler(normalizeState(event.payload))
    })
    this.nativePollHandler = handler
  }

  unlistenForStateChanges(): void {
    this.stateUnlisten?.()
    this.stateUnlisten = null
    this.nativePollHandler = null
    this.stopNativePolling()
  }

  async getState(): Promise<RecordingState> {
    if (!this.isSupported()) return DEFAULT_STATE
    return normalizeState(await invoke<RecordingState>('get_recording_state'))
  }

  async start(autoPauseEnabled: boolean): Promise<RecordingState> {
    if (!this.isSupported()) {
      throw new Error('Track recording is only available in the Tauri mobile app.')
    }

    if (isTauriAndroidMobile()) {
      await ensureGeolocationPermission()
    }

    const state = normalizeState(
      await invoke<RecordingState>('start_recording', { autoPauseEnabled }),
    )

    if (this.usesForegroundWebWriter()) {
      await this.startForegroundWatch()
    }

    return state
  }

  async pause(): Promise<RecordingState> {
    await this.stopForegroundWatch()
    return normalizeState(await invoke<RecordingState>('pause_recording'))
  }

  async resume(): Promise<RecordingState> {
    if (isTauriAndroidMobile()) {
      await ensureGeolocationPermission()
    }

    const state = normalizeState(await invoke<RecordingState>('resume_recording'))

    if (this.usesForegroundWebWriter()) {
      await this.startForegroundWatch()
    }

    return state
  }

  async stop(): Promise<RecordingDraft> {
    await this.stopForegroundWatch()
    return invoke<RecordingDraft>('stop_recording')
  }

  async discard(): Promise<RecordingState> {
    await this.stopForegroundWatch()
    return normalizeState(await invoke<RecordingState>('discard_recording'))
  }

  async readDraft(): Promise<RecordingDraft | null> {
    if (!this.isSupported()) return null
    return invoke<RecordingDraft | null>('read_recording_draft')
  }

  async clearDraft(sessionId: string): Promise<RecordingState> {
    return normalizeState(await invoke<RecordingState>('clear_recording_draft', { sessionId }))
  }

  async appendPoint(point: RecordingPointInput): Promise<RecordingState> {
    return normalizeState(await invoke<RecordingState>('append_recording_point', { point }))
  }

  async setAutoPauseEnabled(enabled: boolean): Promise<RecordingState> {
    return normalizeState(await invoke<RecordingState>('set_recording_auto_pause', { enabled }))
  }

  async resumeForegroundWriterIfNeeded(state: RecordingState): Promise<void> {
    if (this.usesNativePointWriter() && (state.status === 'active' || state.autoPaused)) {
      this.startNativePolling()
      return
    }

    this.stopNativePolling()

    if (!this.usesForegroundWebWriter() || state.status !== 'active') {
      await this.stopForegroundWatch()
      return
    }

    await this.startForegroundWatch()
  }

  async stopForegroundWatch(): Promise<void> {
    const watch = this.foregroundWatch
    this.foregroundWatch = null
    this.foregroundWatchStartPromise = null
    await clearWatch(watch)
  }

  private async startForegroundWatch(): Promise<void> {
    if (this.foregroundWatch) return
    if (this.foregroundWatchStartPromise) return this.foregroundWatchStartPromise

    this.foregroundWatchStartPromise = watchPosition(
      RECORDING_GEOLOCATION_OPTIONS,
      (position) => {
        void this.appendPoint(positionToPointInput(position)).catch((error) => {
          logService.error(LOG_CATEGORIES.GENERAL, 'Failed to persist recording point', { error })
        })
      },
      (error) => this.handleForegroundWatchError(error),
    )
      .then((handle) => {
        this.foregroundWatch = handle
      })
      .finally(() => {
        this.foregroundWatchStartPromise = null
      })

    return this.foregroundWatchStartPromise
  }

  private startNativePolling(): void {
    if (this.nativePollTimer) return

    this.nativePollTimer = setInterval(() => {
      void this.getState()
        .then((state) => {
          this.nativePollHandler?.(state)
          if (state.status !== 'active' && !state.autoPaused) {
            this.stopNativePolling()
          }
        })
        .catch((error) => {
          logService.warn(LOG_CATEGORIES.GENERAL, 'Failed to refresh native recording state', {
            error,
          })
        })
    }, 2000)
  }

  private stopNativePolling(): void {
    if (!this.nativePollTimer) return
    clearInterval(this.nativePollTimer)
    this.nativePollTimer = null
  }

  private handleForegroundWatchError(error: GeoError): void {
    const isDenied = error.code === error.PERMISSION_DENIED
    const message = isDenied
      ? 'Location permission was denied while recording.'
      : 'Location tracking failed while recording.'
    logService.warn(LOG_CATEGORIES.GENERAL, message, { error })
  }
}

export const recordingNativeService = new RecordingNativeService()
